import { create } from "zustand";
import { IAsset, IPoolData } from "@api/IWalletController";
import {
    AviproductionChartApi,
    ChartResolution,
    IPriceChartRequest,
    ICandlestickData,
} from "@api/AviproductionChartApi";

export interface IChartStore {
    chartData: { pair: [IAsset, IAsset] | []; chartData: ICandlestickData[] } | null;
    isLoading: boolean;
    error: string | null;
    selectedTimePeriod: string;
    visibleRange: number;
    chartOptions: {
        timeScale?: {
            tickMarkFormatter?: (time: number) => string;
            [key: string]: any;
        };
        localization: {
            timeFormatter: (businessDayOrTimestamp: any) => string;
            dateFormat?: string;
        };
    };

    setChartData: (chartData: { pair: [IAsset, IAsset]; chartData: ICandlestickData[] }) => void;
    updateChartData: (
        pool: IPoolData,
        pair: [IAsset, IAsset],
        resolution: ChartResolution,
        from: number,
        to: number,
        visibleRange: number,
    ) => Promise<void>;
    updateChartOptions: (options: {
        timeScale?: {
            tickMarkFormatter?: (time: number) => string;
            [key: string]: any;
        };
        localization?: {
            dateFormat?: string;
            timeFormatter?: (businessDayOrTimestamp: any) => string;
        };
    }) => void;
    setTimePeriod: (timePeriod: string) => void;
}

export const useChart = create<IChartStore>((set) => ({
    chartData: null,
    isLoading: false,
    error: null,
    selectedTimePeriod: "1d",
    visibleRange: 0,
    chartOptions: {
        timeScale: {
            rightOffset: 0,
            barSpacing: 6,
            lockVisibleTimeRangeOnResize: false,
            rightBarStaysOnScroll: true,
            borderVisible: false,
            borderColor: "#444444",
            visible: true,
            timeVisible: true,
            secondsVisible: false,
            tickMarkStep: 1,
            tickMarkFormatter: (time: number): string => {
                const date = new Date(time * 1000);
                return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
            },
            minBarSpacing: 1,
        },
        localization: {
            dateFormat: "",
            timeFormatter: (businessDayOrTimestamp: any): string => {
                const time =
                    typeof businessDayOrTimestamp === "number" ? businessDayOrTimestamp : businessDayOrTimestamp.time;
                const date = new Date(time * 1000);
                return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
            },
        },
    },

    setChartData: (chartData) => set({ chartData }),

    updateChartData: async (
        pool: IPoolData,
        pair: [IAsset, IAsset],
        resolution: ChartResolution,
        from: number,
        to: number,
        visibleRange: number,
    ) => {
        set({ isLoading: true, error: null });

        try {
            const request: IPriceChartRequest = {
                address: pool.poolAddress,
                network: "ton",
                res: resolution,
                from,
                to,
            };
            const response = await AviproductionChartApi.getPairPriceChart(request);

            if (response && response !== null) {
                const newChartData = { pair, chartData: response };
                set({ chartData: newChartData, visibleRange });
            } else {
                throw new Error("Failed to fetch chart data");
            }
        } catch (error) {
            set({ error: error instanceof Error ? error.message : "Unknown error" });
        } finally {
            set({ isLoading: false });
        }
    },

    updateChartOptions: (options) => {
        set((state) => ({
            chartOptions: {
                ...state.chartOptions,
                timeScale: {
                    ...state.chartOptions.timeScale,
                    ...options.timeScale,
                    tickMarkFormatter:
                        options.timeScale?.tickMarkFormatter || state.chartOptions.timeScale?.tickMarkFormatter,
                },
                localization: {
                    ...state.chartOptions.localization,
                    ...options.localization,
                    dateFormat: options.localization?.dateFormat || state.chartOptions.localization.dateFormat,
                    timeFormatter: options.localization?.timeFormatter || state.chartOptions.localization.timeFormatter,
                },
            },
        }));
    },

    setTimePeriod: (timePeriod) => {
        set({ selectedTimePeriod: timePeriod });
    },
}));
